/**
 * Globe — MapLibre GL JS with globe projection + Deck.gl overlay for point layers.
 *
 * MapLibre v5 renders all polygon fills/lines natively on the globe (no tessellation artifacts).
 * Deck.gl layers (ScatterplotLayer for POIs) are rendered on top via MapboxOverlay.
 *
 * Polygon rendering uses MapLibre native sources + fill/line layers.
 * Point rendering uses Deck.gl ScatterplotLayer via the MapboxOverlay.
 * City markers use MapLibre native symbol layers with LeafMap-style teardrop pins.
 */
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { MapboxOverlay } from '@deck.gl/mapbox'
import { Protocol } from 'pmtiles'
import DOMPurify from 'dompurify'
import OvertureLayers from '../layers/overture-layers.js'
import {
  isMapTilerKeyRejection,
  FALLBACK_SATELLITE_SOURCE,
  FALLBACK_TERRAIN_SOURCE,
} from './maptiler-key-check.js'

// ── LeafMap-style teardrop marker generation ──────────────────
// Population classification: color + size for each class
const MARKER_CLASSES = [
  { id: 'marker-xs', color: '#4CAF50', size: 28, label: '< 50K' }, // green
  { id: 'marker-sm', color: '#FFC107', size: 34, label: '50K – 200K' }, // amber
  { id: 'marker-md', color: '#FF9800', size: 40, label: '200K – 500K' }, // orange
  { id: 'marker-lg', color: '#F44336', size: 48, label: '500K – 1M' }, // red
  { id: 'marker-xl', color: '#B71C1C', size: 56, label: '1M+' }, // dark red
]

const MARKER_BREAKS = [50000, 200000, 500000, 1000000]

/**
 * Draw a LeafMap-style teardrop marker and return pixel data for MapLibre.
 * Renders at 1x resolution — MapLibre handles scaling via icon-size.
 */
function createTeardropImage(color, size) {
  const w = size
  const h = Math.round(size * 1.4)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')

  const cx = w / 2
  const r = w * 0.38
  const circleY = r + 2
  const tipY = h - 2

  // Draw teardrop shape: circle on top, tapered point at bottom
  ctx.beginPath()
  const tangentAngle = Math.asin(r / (tipY - circleY)) || 0.3
  ctx.arc(cx, circleY, r, Math.PI / 2 + tangentAngle, Math.PI * 2 + Math.PI / 2 - tangentAngle)
  ctx.lineTo(cx, tipY)
  ctx.closePath()

  // Fill with gradient
  const grad = ctx.createLinearGradient(cx - r, 0, cx + r, h)
  grad.addColorStop(0, lightenColor(color, 30))
  grad.addColorStop(0.5, color)
  grad.addColorStop(1, darkenColor(color, 30))
  ctx.fillStyle = grad
  ctx.fill()

  // Border
  ctx.strokeStyle = darkenColor(color, 50)
  ctx.lineWidth = 1.5
  ctx.stroke()

  // White inner dot
  ctx.beginPath()
  ctx.arc(cx, circleY, r * 0.35, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(255,255,255,0.9)'
  ctx.fill()

  const imageData = ctx.getImageData(0, 0, w, h)
  return { data: new Uint8Array(imageData.data.buffer), width: w, height: h }
}

function lightenColor(hex, amt) {
  let [r, g, b] = hexToRgb(hex)
  r = Math.min(255, r + amt)
  g = Math.min(255, g + amt)
  b = Math.min(255, b + amt)
  return `rgb(${r},${g},${b})`
}

function darkenColor(hex, amt) {
  let [r, g, b] = hexToRgb(hex)
  r = Math.max(0, r - amt)
  g = Math.max(0, g - amt)
  b = Math.max(0, b - amt)
  return `rgb(${r},${g},${b})`
}

function hexToRgb(hex) {
  const m = hex.replace('#', '').match(/.{2}/g)
  return m.map((c) => parseInt(c, 16))
}

// MapTiler Satellite + Terrain — commercially licensed
const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_API_KEY || ''
const MAPTILER_SATELLITE_URL = `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`
const MAPTILER_TERRAIN_URL = `https://api.maptiler.com/tiles/terrain-rgb-v2/{z}/{x}/{y}.webp?key=${MAPTILER_KEY}`

const INITIAL_VIEW_STATE = {
  longitude: 0,
  latitude: 20,
  zoom: 1.5,
  pitch: 0,
  bearing: 0,
}

// Auto-rotation speed (degrees per second)
const AUTO_ROTATE_SPEED = 3

class Globe {
  constructor(container) {
    this.container = container
    this.viewState = { ...INITIAL_VIEW_STATE }
    this.autoRotate = true
    this.autoRotateRAF = null
    this.lastTimestamp = null
    this._isFlying = false

    // Native MapLibre layer tracking
    this._mapReady = false
    this._pendingOps = []
    this._nativeLayerIds = new Set()

    this._init()
  }

  /**
   * CL-261: swap the MapTiler basemap for the keyless fallback in place. Keeps
   * the `maptiler-*` source ids (call sites setTerrain by that id) and keeps the
   * satellite layer at the bottom of the stack. Idempotent.
   */
  _useFallbackBasemap() {
    if (this._basemapFallback) return
    this._basemapFallback = true
    const map = this.map
    const apply = () => {
      try {
        const terrain = map.getTerrain()
        if (terrain) map.setTerrain(null)
        const layers = map.getStyle().layers
        const satIdx = layers.findIndex((l) => l.id === 'maptiler-satellite-layer')
        const beforeId = satIdx >= 0 && layers[satIdx + 1] ? layers[satIdx + 1].id : undefined
        if (map.getLayer('maptiler-satellite-layer')) map.removeLayer('maptiler-satellite-layer')
        if (map.getSource('maptiler-satellite')) map.removeSource('maptiler-satellite')
        if (map.getSource('maptiler-terrain')) map.removeSource('maptiler-terrain')
        map.addSource('maptiler-satellite', { ...FALLBACK_SATELLITE_SOURCE })
        map.addSource('maptiler-terrain', { ...FALLBACK_TERRAIN_SOURCE })
        map.addLayer(
          { id: 'maptiler-satellite-layer', type: 'raster', source: 'maptiler-satellite' },
          beforeId
        )
        if (terrain)
          map.setTerrain({ source: 'maptiler-terrain', exaggeration: terrain.exaggeration })
      } catch (err) {
        console.error('[Globe] fallback basemap swap failed', err)
      }
    }
    // Not isStyleLoaded(): that also waits on tiles, and the rejected MapTiler
    // tiles keep it false forever. The style JSON being parsed is enough.
    if (map.style && map.style._loaded) apply()
    else map.once('style.load', apply)
  }

  _init() {
    // P2-3: a missing MapTiler key falls back to '' and every satellite/terrain
    // tile 403s — a silent black globe. Make the misconfig legible.
    if (!MAPTILER_KEY) {
      console.warn(
        '[Globe] VITE_MAPTILER_API_KEY is not set — MapTiler satellite + terrain tiles ' +
          'will 403 and the globe renders black with flat terrain. Set it in .env ' +
          '(see .env.example) and restart the dev server.'
      )
    }

    // Register PMTiles protocol for Overture Maps vector tiles
    const pmtilesProtocol = new Protocol()
    maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile)

    // MapLibre style with MapTiler satellite raster tiles
    const style = {
      version: 8,
      glyphs: 'https://cdn.protomaps.com/fonts/pbf/{fontstack}/{range}.pbf',
      // CL-261: no key → start on the keyless fallback instead of a black globe.
      sources: MAPTILER_KEY
        ? {
            'maptiler-satellite': {
              type: 'raster',
              tiles: [MAPTILER_SATELLITE_URL],
              tileSize: 512,
              maxzoom: 20,
              attribution: '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>',
            },
            'maptiler-terrain': {
              type: 'raster-dem',
              tiles: [MAPTILER_TERRAIN_URL],
              tileSize: 512,
              maxzoom: 12,
            },
          }
        : {
            'maptiler-satellite': { ...FALLBACK_SATELLITE_SOURCE },
            'maptiler-terrain': { ...FALLBACK_TERRAIN_SOURCE },
          },
      terrain: {
        source: 'maptiler-terrain',
        exaggeration: 1.5,
      },
      layers: [
        {
          id: 'maptiler-satellite-layer',
          type: 'raster',
          source: 'maptiler-satellite',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    }

    // Create MapLibre map
    this.map = new maplibregl.Map({
      container: this.container,
      style,
      center: [INITIAL_VIEW_STATE.longitude, INITIAL_VIEW_STATE.latitude],
      zoom: INITIAL_VIEW_STATE.zoom,
      pitch: INITIAL_VIEW_STATE.pitch,
      bearing: INITIAL_VIEW_STATE.bearing,
      antialias: true,
      maxPitch: 85,
    })

    // R-3 (BUG-071): surface a REJECTED MapTiler key (present but 401/403) once,
    // so a black globe reads as "bad key" rather than a mystery. CL-243 keyed
    // only on e.error.status, which MapLibre doesn't reliably set for raster-tile
    // failures — isMapTilerKeyRejection() also inspects the message/url. Not gated
    // on DEV: a rejected key in production deserves the error too.
    this._maptilerKeyWarned = false
    this.map.on('error', (e) => {
      if (this._maptilerKeyWarned) return
      if (isMapTilerKeyRejection(e)) {
        this._maptilerKeyWarned = true
        console.error(
          '[Globe] MapTiler tiles were rejected (401/403) — VITE_MAPTILER_API_KEY in .env is ' +
            'set but invalid/expired/over quota. ' +
            'Get a key at https://cloud.maptiler.com/account/keys/ and refresh VITE_MAPTILER_API_KEY. ' +
            'Switching to the keyless NASA Blue Marble fallback basemap.'
        )
        this._useFallbackBasemap()
      }
    })

    // Enable globe projection once style loads
    this.map.on('style.load', () => {
      this.map.setProjection({ type: 'globe' })
    })

    // Create Deck.gl overlay for point layers (ScatterplotLayer)
    this.deckOverlay = new MapboxOverlay({
      interleaved: true,
      layers: [],
    })
    this.map.addControl(this.deckOverlay)

    // CL-167 (roadmap H3.1, Grade 4): metric scale bar — the Grade-4 "scale"
    // objective needs a real, visible scale that changes as students zoom.
    // CL-168: bottom-LEFT (bottom-right is display:none — the lesson panel
    // side); styles.css lifts the container above the bottom command bar.
    this.map.addControl(
      new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }),
      'bottom-left'
    )

    // Stop auto-rotation on user interaction
    this.map.on('mousedown', () => this.stopAutoRotate())
    this.map.on('touchstart', () => this.stopAutoRotate())
    this.map.on('wheel', () => this.stopAutoRotate())

    // Feature click popup
    this._popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'ev-popup',
      maxWidth: '260px',
    })

    this.map.on('click', (e) => this._handleFeatureClick(e))
    this.map.on('mousemove', (e) => this._handleFeatureHover(e))

    // Track view state changes
    this.map.on('move', () => {
      const center = this.map.getCenter()
      this.viewState = {
        longitude: center.lng,
        latitude: center.lat,
        zoom: this.map.getZoom(),
        pitch: this.map.getPitch(),
        bearing: this.map.getBearing(),
      }
    })

    // Mark map as ready, load marker images, and flush pending operations
    this.map.on('load', () => {
      this._loadMarkerImages()
      this._mapReady = true
      this.overture = new OvertureLayers(this)
      for (const op of this._pendingOps) op()
      this._pendingOps = []
      this.startAutoRotate()
    })
  }

  /**
   * Get all interactive layer IDs (fill layers + symbol layers).
   */
  _getInteractiveLayers() {
    const layers = []
    for (const id of this._nativeLayerIds) {
      // Symbol layers first (markers should take priority over lines/polygons beneath them)
      if (this.map.getLayer(`${id}-symbols`)) layers.push(`${id}-symbols`)
      // POI circle layers (points of interest)
      if (this.map.getLayer(`${id}-circle`)) layers.push(`${id}-circle`)
      // Hit-target layers for lines (wider click area than the visible line)
      if (this.map.getLayer(`${id}-hit`)) layers.push(`${id}-hit`)
      if (this.map.getLayer(`${id}-fill`)) layers.push(`${id}-fill`)
    }
    // Add Overture vector tile interactive layers
    if (this.overture) {
      layers.push(...this.overture.getInteractiveLayers())
    }
    return layers
  }

  /**
   * Handle click on map — show popup for polygons or city markers.
   */
  _handleFeatureClick(e) {
    const interactiveLayers = this._getInteractiveLayers()
    if (interactiveLayers.length === 0) return

    const features = this.map.queryRenderedFeatures(e.point, { layers: interactiveLayers })
    if (!features || features.length === 0) {
      this._popup.remove()
      return
    }

    const feature = features[0]
    const props = feature.properties
    const layerType = feature.layer?.type
    const layerId = feature.layer?.id || ''

    // Detect feature type: marker (symbol), POI (circle), line hit-target, Overture vector tile, or polygon
    const isMarker = layerType === 'symbol'
    const isPOI = layerType === 'circle' || layerId.endsWith('-circle')
    const isLine = layerId.endsWith('-hit')
    const isOverture = layerId.startsWith('overture-')

    if (isOverture) {
      this._showOverturePopup(e.lngLat, props, layerId)
    } else if (isMarker) {
      this._showMarkerPopup(e.lngLat, props)
    } else if (isPOI) {
      this._showPOIPopup(e.lngLat, props)
    } else if (isLine) {
      this._showLinePopup(e.lngLat, props)
    } else {
      this._showPolygonPopup(e.lngLat, props)
    }
  }

  /**
   * Show popup for a city/capital marker.
   */
  _showMarkerPopup(lngLat, props) {
    const name = props._label || props.NAME || props.name || 'Unknown'
    const state = props.ADM1NAME || props.adm1name || ''
    const country = props.ADM0NAME || props.adm0name || ''
    const pop = props.POP_MAX ?? props.pop_max

    const rows = []
    // City name as header
    rows.push(`<div class="ev-popup-name">${name}</div>`)

    // State / country subtitle
    const subtitle = [state, country].filter(Boolean).join(', ')
    if (subtitle) {
      rows.push(`<div class="ev-popup-subtitle">${subtitle}</div>`)
    }

    // Population (city proper)
    if (pop != null && !isNaN(pop)) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Population</span><span class="ev-popup-value">${Number(pop).toLocaleString()}</span></div>`
      )
    }

    // Classification
    const featurecla = props.featurecla
    if (featurecla) {
      const label = featurecla.replace('Admin-0', 'National').replace('Admin-1', 'State')
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${label}</span></div>`
      )
    }

    this._popup
      .setLngLat(lngLat)
      .setHTML(DOMPurify.sanitize(rows.join('')))
      .addTo(this.map)
  }

  /**
   * Show popup for a line feature (river, road, route).
   */
  _showLinePopup(lngLat, props) {
    const name = props.name_en || props.name || props.NAME || 'Unknown'
    const featurecla = props.featurecla || ''

    const rows = []
    rows.push(`<div class="ev-popup-name">${name}</div>`)

    // Feature type
    if (featurecla) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${featurecla}</span></div>`
      )
    }

    // Scale rank (importance)
    const rank = props.scalerank
    if (rank != null) {
      const importance = rank <= 2 ? 'Major' : rank <= 4 ? 'Significant' : 'Minor'
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Importance</span><span class="ev-popup-value">${importance}</span></div>`
      )
    }

    // Alternate name
    const altName = props.name_alt
    if (altName) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Also known as</span><span class="ev-popup-value">${altName}</span></div>`
      )
    }

    this._popup
      .setLngLat(lngLat)
      .setHTML(DOMPurify.sanitize(rows.join('')))
      .addTo(this.map)
  }

  /**
   * Show popup for a POI marker (museum, school, hospital, etc.).
   */
  _showPOIPopup(lngLat, props) {
    const name = props.name || 'Unknown'
    const rows = []
    rows.push(`<div class="ev-popup-name">${name}</div>`)

    // City as subtitle
    if (props.city) {
      rows.push(`<div class="ev-popup-subtitle">${props.city}</div>`)
    }

    // Category
    if (props.category) {
      const label = props.category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Category</span><span class="ev-popup-value">${label}</span></div>`
      )
    }

    this._popup
      .setLngLat(lngLat)
      .setHTML(DOMPurify.sanitize(rows.join('')))
      .addTo(this.map)
  }

  /**
   * Show popup for an Overture Maps vector tile feature.
   */
  _showOverturePopup(lngLat, props, layerId) {
    // Overture tiles use @name for the display name
    const name = props['@name'] || props.name || 'Unknown'
    const rows = []
    rows.push(`<div class="ev-popup-name">${name}</div>`)

    if (layerId.includes('places')) {
      // POI from Overture places — categories is a JSON string
      if (props.categories) {
        try {
          const cats = JSON.parse(props.categories)
          const main = cats.main || cats.primary
          if (main) {
            const label = String(main)
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase())
            rows.push(
              `<div class="ev-popup-row"><span class="ev-popup-label">Category</span><span class="ev-popup-value">${label}</span></div>`
            )
          }
        } catch {
          // If not valid JSON, show raw
          const label = String(props.categories)
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
          rows.push(
            `<div class="ev-popup-row"><span class="ev-popup-label">Category</span><span class="ev-popup-value">${label}</span></div>`
          )
        }
      }
      if (props.addresses) {
        try {
          const addrs = JSON.parse(props.addresses)
          const first = Array.isArray(addrs) ? addrs[0] : addrs
          const parts = [first?.freeform, first?.locality, first?.region].filter(Boolean)
          if (parts.length) {
            rows.push(
              `<div class="ev-popup-row"><span class="ev-popup-label">Address</span><span class="ev-popup-value">${parts.join(', ')}</span></div>`
            )
          }
        } catch {
          /* skip */
        }
      }
      if (props.brand) {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Brand</span><span class="ev-popup-value">${props.brand}</span></div>`
        )
      }
    } else if (layerId.includes('water')) {
      // Water feature
      if (props.subtype) {
        const label = String(props.subtype)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
      if (props.class && props.class !== props.subtype) {
        const label = String(props.class)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Class</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
      if (props.is_salt) {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Salt water</span><span class="ev-popup-value">Yes</span></div>`
        )
      }
      if (props.is_intermittent) {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Seasonal</span><span class="ev-popup-value">Yes</span></div>`
        )
      }
    } else if (layerId.includes('land')) {
      // Land or land use feature
      if (props.subtype) {
        const label = String(props.subtype)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
      if (props.class && props.class !== props.subtype) {
        const label = String(props.class)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Class</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
    } else if (layerId.includes('road') || layerId.includes('rail')) {
      // Transportation
      if (props.class) {
        const label = String(props.class)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Class</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
      if (props.subtype && props.subtype !== 'road') {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${props.subtype}</span></div>`
        )
      }
    } else if (layerId.includes('building')) {
      // Building — show class, height, floors with smart estimation
      if (props.class) {
        const label = String(props.class)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Type</span><span class="ev-popup-value">${label}</span></div>`
        )
      }
      if (props.height) {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Height</span><span class="ev-popup-value">${Math.round(props.height)}m</span></div>`
        )
      } else if (props.num_floors) {
        const est = Math.round(props.num_floors * 3.5)
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Height</span><span class="ev-popup-value">~${est}m (est.)</span></div>`
        )
      }
      if (props.num_floors) {
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">Floors</span><span class="ev-popup-value">${props.num_floors}</span></div>`
        )
      }
    }

    this._popup
      .setLngLat(lngLat)
      .setHTML(DOMPurify.sanitize(rows.join('')))
      .addTo(this.map)
  }

  /**
   * Show popup for a polygon (country, state, admin boundary).
   */
  _showPolygonPopup(lngLat, props) {
    const name =
      props.name_en || props.NAME || props.name || props.shapeName || props.admin || 'Unknown'

    // Custom popup builder (set by specific ops like un-treaties)
    if (this._customPopupBuilder) {
      const html = this._customPopupBuilder(name, props)
      if (html) {
        this._popup.setLngLat(lngLat).setHTML(DOMPurify.sanitize(html)).addTo(this.map)
        return
      }
    }

    const rows = []
    rows.push(`<div class="ev-popup-name">${name}</div>`)

    // Active choropleth attribute (e.g. HDI, life expectancy, GDP per capita)
    if (this._activeChoropleth) {
      const { attribute, displayName } = this._activeChoropleth
      const val = props[attribute]
      if (val != null) {
        let formatted
        if (!isNaN(val) && val !== '') {
          const numVal = Number(val)
          // Format based on value range
          if (numVal >= 1_000_000) {
            formatted = numVal.toLocaleString(undefined, { maximumFractionDigits: 0 })
          } else if (numVal >= 100) {
            formatted = numVal.toLocaleString(undefined, { maximumFractionDigits: 1 })
          } else if (numVal >= 1) {
            formatted = numVal.toLocaleString(undefined, { maximumFractionDigits: 2 })
          } else {
            formatted = numVal.toLocaleString(undefined, { maximumFractionDigits: 3 })
          }
        } else {
          // Text value (e.g. "Free", "Not Free", "Tropical Rainforest")
          formatted = String(val)
        }
        rows.push(
          `<div class="ev-popup-row"><span class="ev-popup-label">${displayName}</span><span class="ev-popup-value">${formatted}</span></div>`
        )
      }
    }

    // Population
    const pop = props.population ?? props.POP_EST ?? props.pop_est
    if (pop != null && !isNaN(pop)) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Population</span><span class="ev-popup-value">${Number(pop).toLocaleString()}</span></div>`
      )
    }

    // GDP
    const gdp = props.GDP_MD ?? props.gdp_md
    if (gdp != null && !isNaN(gdp)) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">GDP</span><span class="ev-popup-value">$${Number(gdp).toLocaleString()}M</span></div>`
      )
    }

    // Area
    const area = props.area_km2 ?? props.AREA_KM2
    if (area != null && !isNaN(area)) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Area</span><span class="ev-popup-value">${Number(area).toLocaleString()} km²</span></div>`
      )
    }

    // Density
    const density = props.pop_density_km2 ?? props.pop_density
    if (density != null && !isNaN(density)) {
      rows.push(
        `<div class="ev-popup-row"><span class="ev-popup-label">Density</span><span class="ev-popup-value">${Number(density).toLocaleString()} /km²</span></div>`
      )
    }

    this._popup
      .setLngLat(lngLat)
      .setHTML(DOMPurify.sanitize(rows.join('')))
      .addTo(this.map)
  }

  /**
   * Change cursor to pointer when hovering over interactive features.
   */
  _handleFeatureHover(e) {
    const interactiveLayers = this._getInteractiveLayers()
    if (interactiveLayers.length === 0) {
      this.map.getCanvas().style.cursor = ''
      return
    }

    const features = this.map.queryRenderedFeatures(e.point, { layers: interactiveLayers })
    this.map.getCanvas().style.cursor = features?.length > 0 ? 'pointer' : ''
  }

  /**
   * Execute a function when the map is ready, or queue it.
   */
  _exec(fn) {
    if (this._mapReady) {
      fn()
    } else {
      this._pendingOps.push(fn)
    }
  }

  /**
   * Pre-load teardrop marker images for all population classes.
   */
  _loadMarkerImages() {
    for (const mc of MARKER_CLASSES) {
      if (this.map.hasImage(mc.id)) continue
      const imgData = createTeardropImage(mc.color, mc.size)
      this.map.addImage(mc.id, imgData)
    }
    console.log(
      '[Globe] Loaded teardrop marker images:',
      MARKER_CLASSES.map((m) => m.id)
    )
  }

  // ── Native MapLibre polygon layers ─────────────────────────

  /**
   * Add a native MapLibre polygon layer (fill + line).
   * Used for choropleths, highlights, and admin boundaries.
   *
   * @param {string} id - Layer ID prefix (e.g., 'data-choropleth', 'boundary')
   * @param {object} geojson - GeoJSON FeatureCollection
   * @param {object} options - { fill: paint object, line: paint object }
   *   Set fill or line to false to skip that sublayer.
   */
  addPolygonLayer(id, geojson, options = {}) {
    this._exec(() => {
      try {
        // Remove existing layer with this ID first
        this._removeNativeSync(id)

        const sourceId = `${id}-src`
        console.log(
          `[Globe] addPolygonLayer "${id}": ${geojson?.features?.length ?? 0} features, fill=${!!options.fill}, line=${!!options.line}`
        )

        this.map.addSource(sourceId, {
          type: 'geojson',
          data: geojson,
          ...(options.attribution ? { attribution: options.attribution } : {}),
        })

        if (options.fill) {
          this.map.addLayer({
            id: `${id}-fill`,
            type: 'fill',
            source: sourceId,
            paint: options.fill,
          })
        }

        if (options.line) {
          this.map.addLayer({
            id: `${id}-line`,
            type: 'line',
            source: sourceId,
            paint: options.line,
          })
        }

        // Optional centroid text labels (opt-in via options.label === true, so
        // choropleths/boundaries — which don't pass it — stay unlabelled). Used
        // by polygon physical features (oceans, deserts, lakes, …) to print the
        // feature name on the map. _removeNativeSync already cleans `${id}-label`.
        if (options.label === true) {
          const labelField = options.labelField || 'name'
          this.map.addLayer({
            id: `${id}-label`,
            type: 'symbol',
            source: sourceId,
            layout: {
              'text-field': ['get', labelField],
              'text-font': ['Noto Sans Regular'],
              'text-size': 13,
              'text-allow-overlap': false,
              'text-ignore-placement': false,
            },
            paint: {
              'text-color': options.labelColor || '#ffffff',
              'text-halo-color': 'rgba(0,0,0,0.85)',
              'text-halo-width': 1.5,
            },
          })
        }

        this._nativeLayerIds.add(id)
        console.log(`[Globe] addPolygonLayer "${id}" — success. Active native layers:`, [
          ...this._nativeLayerIds,
        ])
      } catch (err) {
        console.error(`[Globe] addPolygonLayer "${id}" FAILED:`, err)
      }
    })
  }

  /**
   * Add a native MapLibre line layer (rivers, roads, routes).
   * Renders LineString/MultiLineString features with optional text labels along the line.
   *
   * @param {string} id - Layer ID prefix
   * @param {object} geojson - GeoJSON FeatureCollection with LineString/MultiLineString geometries
   * @param {object} options - { line: paint object, label: boolean, labelField: string }
   */
  addLineLayer(id, geojson, options = {}) {
    this._exec(() => {
      try {
        this._removeNativeSync(id)
        const sourceId = `${id}-src`
        console.log(`[Globe] addLineLayer "${id}": ${geojson?.features?.length ?? 0} features`)

        this.map.addSource(sourceId, { type: 'geojson', data: geojson })

        // Invisible wide hit-target layer for easy clicking
        this.map.addLayer({
          id: `${id}-hit`,
          type: 'line',
          source: sourceId,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': 'rgba(0,0,0,0)', 'line-width': 12, 'line-opacity': 0 },
        })

        // Visible line layer
        this.map.addLayer({
          id: `${id}-line`,
          type: 'line',
          source: sourceId,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: options.line || {
            'line-color': '#4fc3f7',
            'line-width': 1.5,
            'line-opacity': 0.8,
          },
        })

        // Optional text labels along the line
        if (options.label !== false) {
          const labelField = options.labelField || 'name'
          this.map.addLayer({
            id: `${id}-labels`,
            type: 'symbol',
            source: sourceId,
            layout: {
              'symbol-placement': 'line',
              'text-field': ['get', labelField],
              'text-font': ['Noto Sans Regular'],
              'text-size': 11,
              'text-allow-overlap': false,
              'text-ignore-placement': false,
              'text-max-angle': 30,
              'text-offset': [0, 0.6],
            },
            paint: {
              'text-color': options.labelColor || '#b3e5fc',
              'text-halo-color': 'rgba(0,0,0,0.85)',
              'text-halo-width': 1.5,
            },
          })
        }

        this._nativeLayerIds.add(id)
      } catch (err) {
        console.error(`[Globe] addLineLayer "${id}" FAILED:`, err)
      }
    })
  }

  /**
   * Add a native MapLibre circle layer for POI markers.
   * Uses circle type for efficient rendering of thousands of points
   * with per-feature category colors and full click support.
   */
  addPOILayer(id, geojson, options = {}) {
    this._exec(() => {
      try {
        this._removeNativeSync(id)
        const sourceId = `${id}-src`
        console.log(`[Globe] addPOILayer "${id}": ${geojson?.features?.length ?? 0} features`)

        this.map.addSource(sourceId, { type: 'geojson', data: geojson })

        // Circle layer — color driven by _color property per feature
        this.map.addLayer({
          id: `${id}-circle`,
          type: 'circle',
          source: sourceId,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 2, 6, 4, 10, 6, 14, 10],
            'circle-color': ['get', '_color'],
            'circle-opacity': 0.85,
            'circle-stroke-width': 1,
            'circle-stroke-color': 'rgba(255,255,255,0.7)',
          },
        })

        this._nativeLayerIds.add(id)
      } catch (err) {
        console.error(`[Globe] addPOILayer "${id}" FAILED:`, err)
      }
    })
  }

  /**
   * Add a native MapLibre symbol layer (teardrop markers + text labels).
   * Used for city/place markers with population-classified icons.
   *
   * Each GeoJSON feature must have:
   *   _marker: marker image ID (e.g., 'marker-sm')
   *   _markerSize: icon size multiplier (0.0 – 1.0)
   *   _label: text label string
   */
  addMarkerLayer(id, geojson, options = {}) {
    this._exec(() => {
      try {
        this._removeNativeSync(id)
        const sourceId = `${id}-src`
        console.log(`[Globe] addMarkerLayer "${id}": ${geojson?.features?.length ?? 0} features`)

        this.map.addSource(sourceId, { type: 'geojson', data: geojson })

        // Symbol layer: teardrop icon
        this.map.addLayer({
          id: `${id}-symbols`,
          type: 'symbol',
          source: sourceId,
          layout: {
            'icon-image': ['get', '_marker'],
            'icon-size': ['get', '_markerSize'],
            'icon-anchor': 'bottom',
            'icon-allow-overlap': options.allowOverlap ?? true,
            'icon-ignore-placement': false,
            'symbol-sort-key': ['get', '_sortKey'],
          },
          paint: {
            'icon-opacity': 1,
          },
        })

        // Text label layer
        this.map.addLayer({
          id: `${id}-labels`,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['get', '_label'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 12,
            'text-offset': [0, 0.8],
            'text-anchor': 'top',
            'text-optional': true,
            'text-allow-overlap': false,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.8)',
            'text-halo-width': 1.5,
          },
        })

        this._nativeLayerIds.add(id)
      } catch (err) {
        console.error(`[Globe] addMarkerLayer "${id}" FAILED:`, err)
      }
    })
  }

  /**
   * Remove a native polygon layer by ID.
   */
  removePolygonLayer(id) {
    this._exec(() => this._removeNativeSync(id))
  }

  _removeNativeSync(id) {
    try {
      // Remove all layer variants before removing the source
      if (this.map.getLayer(id)) this.map.removeLayer(id) // bare id (e.g. physical feature circles)
      if (this.map.getLayer(`${id}-fill`)) this.map.removeLayer(`${id}-fill`)
      if (this.map.getLayer(`${id}-line`)) this.map.removeLayer(`${id}-line`)
      if (this.map.getLayer(`${id}-hit`)) this.map.removeLayer(`${id}-hit`)
      if (this.map.getLayer(`${id}-circle`)) this.map.removeLayer(`${id}-circle`)
      if (this.map.getLayer(`${id}-symbols`)) this.map.removeLayer(`${id}-symbols`)
      if (this.map.getLayer(`${id}-label`)) this.map.removeLayer(`${id}-label`) // singular (physical features)
      if (this.map.getLayer(`${id}-labels`)) this.map.removeLayer(`${id}-labels`) // plural (other layers)
      if (this.map.getLayer(`${id}-glow`)) this.map.removeLayer(`${id}-glow`) // ocean current glow halo
      if (this.map.getLayer(`${id}-arrows`)) this.map.removeLayer(`${id}-arrows`) // ocean current arrows
      if (this.map.getLayer(`${id}-clusters`)) this.map.removeLayer(`${id}-clusters`) // cluster bubbles
      if (this.map.getLayer(`${id}-cluster-count`)) this.map.removeLayer(`${id}-cluster-count`) // cluster labels
      if (this.map.getSource(`${id}-src`)) this.map.removeSource(`${id}-src`)
    } catch (e) {
      /* layer may not exist yet */
    }
    this._nativeLayerIds.delete(id)
  }

  /**
   * Clear all native layers matching a prefix, or all if no prefix.
   */
  clearNativeLayers(prefix) {
    this._exec(() => {
      for (const id of [...this._nativeLayerIds]) {
        if (!prefix || id.startsWith(prefix)) {
          this._removeNativeSync(id)
        }
      }
    })
  }

  // ── Boundary layers (native MapLibre) ──────────────────────

  /**
   * Show boundary layers on the globe.
   * @param {object} geojson - GeoJSON FeatureCollection (may have _fill properties)
   * @param {object} options - { fill, line } MapLibre paint objects
   */
  showBoundaries(geojson, options = {}) {
    this.clearBoundaries()
    this.addPolygonLayer('boundary', geojson, options)
  }

  /**
   * Clear all boundary layers.
   */
  clearBoundaries() {
    this.clearNativeLayers('boundary')
  }

  // ── Data layers (Deck.gl overlay for points) ──────────────

  /**
   * Update Deck.gl overlay layers (ScatterplotLayer for POIs, etc.).
   */
  updateLayers(deckLayers) {
    this.deckOverlay.setProps({ layers: deckLayers })
  }

  // ── Camera ─────────────────────────────────────────────────

  /**
   * Set view state with smooth transition (MapLibre flyTo).
   */
  setViewState(newViewState, transitionDuration = 2000) {
    this.stopAutoRotate()

    const target = { ...this.viewState, ...newViewState }

    this._isFlying = true
    this.map.flyTo({
      center: [target.longitude, target.latitude],
      zoom: target.zoom,
      pitch: target.pitch ?? this.viewState.pitch,
      bearing: target.bearing ?? this.viewState.bearing,
      duration: transitionDuration,
      essential: true,
    })

    this.map.once('moveend', () => {
      this._isFlying = false
    })
  }

  /**
   * Fly to a specific location.
   */
  flyTo(longitude, latitude, zoom, duration) {
    if (!duration) {
      const dlng = Math.abs(this.viewState.longitude - longitude)
      const dlat = Math.abs(this.viewState.latitude - latitude)
      const dist = Math.sqrt(dlng * dlng + dlat * dlat)
      duration = Math.min(Math.max(dist * 20, 1000), 3000)
    }

    this.setViewState({ longitude, latitude, zoom: zoom || 4 }, duration)
  }

  /**
   * Fit the camera to a bounding box [minLng, minLat, maxLng, maxLat].
   */
  fitToBounds(bbox, options = {}) {
    this.stopAutoRotate()
    const bounds = [
      [bbox[0], bbox[1]],
      [bbox[2], bbox[3]],
    ]
    const padding = options.padding ?? 80
    const maxZoom = options.maxZoom ?? 8
    const duration = options.duration ?? 2000
    const zoomOut = options.zoomOut ?? 0

    // fitBounds alone frames an extent edge-to-edge, which reads as "too
    // close" for administrative units — the surrounding country context that
    // makes a subdivision legible is cropped away. cameraForBounds gives us
    // the fitted camera so we can back it off by a fixed number of zoom
    // levels before flying.
    if (zoomOut > 0 && typeof this.map.cameraForBounds === 'function') {
      try {
        const cam = this.map.cameraForBounds(bounds, { padding })
        if (cam && Number.isFinite(cam.zoom)) {
          this.map.flyTo({
            center: cam.center,
            zoom: Math.max(1, Math.min(cam.zoom - zoomOut, maxZoom)),
            duration,
            essential: true,
          })
          return
        }
      } catch {
        /* fall through to plain fitBounds */
      }
    }

    this.map.fitBounds(bounds, { padding, maxZoom, duration, essential: true })
  }

  /**
   * Reset to initial view.
   */
  /**
   * CL-262: keep framed content clear of a left-side panel (the result table)
   * by shifting the camera's visual centre with MapLibre padding. Every later
   * flyTo/fitBounds keeps this padding. If the camera is mid-flight, wait for
   * it to land rather than interrupt it; only the latest inset is applied.
   * @param {number|function(): number} inset — width (px) of the obscured strip
   *   on the left, 0 = none. A function is measured when the shift is applied,
   *   so panels that move after opening (the layer sidebar) are accounted for.
   */
  setLeftInset(inset) {
    this._leftInset = inset
    if (this._insetPending) return
    const apply = () => {
      this._insetPending = false
      const raw = typeof this._leftInset === 'function' ? this._leftInset() : this._leftInset
      const left = Math.max(0, Math.round(raw || 0))
      const current = this.map.getPadding()?.left || 0
      if (Math.abs(current - left) < 1) return
      this.map.easeTo({
        padding: { top: 0, right: 0, bottom: 0, left },
        duration: 700,
      })
    }
    if (this.map.isMoving()) {
      this._insetPending = true
      this.map.once('moveend', apply)
    } else {
      apply()
    }
  }

  resetView() {
    this.setViewState(INITIAL_VIEW_STATE, 2000)
    setTimeout(() => this.startAutoRotate(), 2500)
  }

  // ── Auto-rotation ─────────────────────────────────────────

  startAutoRotate() {
    if (this.autoRotateRAF) return
    this.autoRotate = true
    this.lastTimestamp = performance.now()

    const animate = (timestamp) => {
      if (!this.autoRotate) return
      if (this._isFlying) {
        this.autoRotateRAF = requestAnimationFrame(animate)
        return
      }

      const delta = (timestamp - this.lastTimestamp) / 1000
      this.lastTimestamp = timestamp

      const center = this.map.getCenter()
      this.map.setCenter([center.lng + AUTO_ROTATE_SPEED * delta, center.lat])

      this.autoRotateRAF = requestAnimationFrame(animate)
    }

    this.autoRotateRAF = requestAnimationFrame(animate)
  }

  stopAutoRotate() {
    this.autoRotate = false
    if (this.autoRotateRAF) {
      cancelAnimationFrame(this.autoRotateRAF)
      this.autoRotateRAF = null
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────

  destroy() {
    this.stopAutoRotate()
    this.map.removeControl(this.deckOverlay)
    this.map.remove()
  }
}

export { Globe, INITIAL_VIEW_STATE, MARKER_CLASSES, MARKER_BREAKS }
export default Globe
